// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {fileInfoFromName} from '@wandb/weave/common/components/FileBrowser';
import WandbLoader from '@wandb/weave/common/components/WandbLoader';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon as WeaveIcon, IconNames} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {clsx} from 'clsx';
import _ from 'lodash';
import numeral from 'numeral';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import TimeAgo from 'react-timeago';
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Checkbox,
  Header,
  Icon,
  Pagination,
  Search,
  SearchProps,
  Table,
} from 'semantic-ui-react';
import useResizeObserver from 'use-resize-observer';

import {EmptyStorageTable} from '../pages/Billing/AccountSettings/ManageUsagePage/EmptyStorageTable';
import {SelectableTableRow} from '../pages/Billing/AccountSettings/ManageUsagePage/SelectableTableRow';
import {
  DEFAULT_COMPARE_FUNCTION,
  DEFAULT_HEADER_CELLS,
  HEADER_BACKGROUND,
} from '../pages/Billing/AccountSettings/ManageUsagePage/StorageHeader';
import {StorageList} from '../pages/Billing/AccountSettings/ManageUsagePage/StorageList';
import {StorageTableBody} from '../pages/Billing/AccountSettings/ManageUsagePage/StorageTableBody';
import {
  File,
  HeaderCellType,
  Node,
  StorageTableContext,
  StorageTableDispatchContext,
} from '../pages/Billing/AccountSettings/ManageUsagePage/StorageTableContextProvider';
import {StorageTableLoadingFailure} from '../pages/Billing/AccountSettings/ManageUsagePage/StorageTableLoadingFailure';
import {StorageTableRowLoading} from '../pages/Billing/AccountSettings/ManageUsagePage/StorageTableRowLoading';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {TableCheckbox} from '../pages/Billing/AccountSettings/ManageUsagePage/TableCheckbox';
import {useIsTextOverflowing} from '../util/hooks/useIsTextOverflowing';
import * as Url from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';

export const CHECKBOX_STYLE =
  'flex h-24 w-24 items-center justify-center rounded-[3px] hover:bg-oblivion/5 cursor-pointer';
export const TABLE_STYLE =
  '!mt-0 !rounded-b-lg !rounded-t-none !border-x-0 !border-b-0';
export const ROW_SELECTED_STYLE = 'bg-teal-300/30 hover:bg-teal-300/30';
export const ROW_DEFAULT_STYLE = 'bg-white hover:bg-moon-50';
const BREADCRUMBS_CLICKABLE_TEXT = 'text-teal-600';

interface FileBrowserProps {
  root: Node | undefined; // root could be undefined if the file path in the URL doesn't match the storage tree and the folder isn't found
  path?: string;
  entityName: string;
  selectionStatus: string;
  deleteButton?: React.ReactNode;
  loadingFiles?: boolean;
  loadingProjects?: boolean;
  loadingArtifacts?: boolean;
  isStorageTable?: boolean;
  orgName?: string;
  hasLoadingError?: boolean;
  onSearchChange?(e: React.MouseEvent, search: SearchProps): void;
  resetQuery?(): void;
  setFilePath(path: string[]): void;
}

const PAGE_SIZE = 25;

type BreadcrumbProps = {
  breadcrumbs: string[];
  i: number;
  path: string[];
  title: string;
  setFilePath(path: string[]): void;
  resetQuery?(): void;
};

const Breadcrumb = ({
  breadcrumbs,
  i,
  setFilePath,
  path,
  resetQuery,
  title,
}: BreadcrumbProps) => {
  const parentDomRef = useRef<HTMLDivElement | null>(null);
  const isTextOverflowing = useIsTextOverflowing({
    text: title,
    parentDomRef,
  });

  const {ref: triggerDomRef} = useResizeObserver<HTMLDivElement>();

  const breadcrumb = (
    <div
      data-test="breadcrumb"
      className={clsx(
        'max-w-[10ch] truncate px-2 text-base/[140%]',
        i < breadcrumbs.length - 1
          ? clsx('cursor-pointer font-semibold', BREADCRUMBS_CLICKABLE_TEXT)
          : 'cursor-auto text-moon-800'
      )}
      ref={triggerDomRef}
      onClick={() => {
        if (i < breadcrumbs.length - 1) {
          setFilePath(path.slice(0, i));
          if (i === 0) {
            resetQuery?.();
          }
        }
      }}>
      {title}
    </div>
  );
  return isTextOverflowing ? (
    <div ref={parentDomRef}>
      <Tooltip content={title} trigger={breadcrumb} />
    </div>
  ) : (
    breadcrumb
  );
};

const FileBrowser = (props: FileBrowserProps) => {
  const history = useHistory();
  const {
    root,
    entityName,
    selectionStatus,
    deleteButton,
    setFilePath,
    loadingFiles,
    loadingProjects,
    loadingArtifacts,
    onSearchChange,
    resetQuery,
    isStorageTable = false,
    orgName,
    hasLoadingError,
  } = props;
  const path = Url.parseRunTabPath(props.path);
  let currentFolder: Node = root ?? {
    files: [],
    subdirectories: {},
  }; // root could be undefined if the url path doesn't exist. This will show the empty state in that case.
  // traverse the tree to the current directory
  path.forEach(folderName => {
    if (currentFolder?.subdirectories[folderName] != null) {
      currentFolder = currentFolder.subdirectories[folderName];
    }
  });
  const dispatch = useContext(StorageTableDispatchContext);

  const breadcrumbs = [entityName].concat(path);

  if (loadingFiles && !isStorageTable) {
    return <Loader name="loading-files-loader" />;
  }

  return isStorageTable ? (
    <>
      <div className="flex justify-between">
        <div className="m-8 flex h-32 items-center">
          <div
            className={clsx(
              'cursor-pointer font-semibold',
              BREADCRUMBS_CLICKABLE_TEXT
            )}
            onClick={() => {
              history.push(
                Url.accountSettingsUsageStorageTeamPage(
                  orgName ?? entityName,
                  ''
                )
              );
              dispatch({type: 'clear'});
            }}>
            Your teams
          </div>
          {breadcrumbs.map((title, i) => (
            <div
              key={i}
              className={
                breadcrumbs.length > 1 ? 'hidden last:flex md:flex' : 'flex'
              }>
              <div className="px-2">/</div>
              {breadcrumbs.length > 1 && (
                <>
                  <Tooltip
                    trigger={
                      <div
                        className={clsx(
                          BREADCRUMBS_CLICKABLE_TEXT,
                          'cursor-pointer font-semibold md:hidden'
                        )}
                        onClick={() =>
                          setFilePath(path.slice(0, breadcrumbs.length - 2))
                        }>
                        ...
                      </div>
                    }
                    content={breadcrumbs
                      .slice(0, breadcrumbs.length - 1)
                      .join(' / ')}
                  />
                  <div className="px-2 md:hidden">/</div>
                </>
              )}
              <Breadcrumb
                breadcrumbs={breadcrumbs}
                i={i}
                setFilePath={setFilePath}
                path={path}
                title={title}
              />
            </div>
          ))}
        </div>
        <div className="p-8">{deleteButton}</div>
      </div>
      <Folder
        folderStatus={selectionStatus}
        onSearchChange={onSearchChange}
        loadingFiles={loadingFiles}
        loadingProjects={loadingProjects}
        loadingArtifacts={loadingArtifacts}
        folder={currentFolder}
        path={path}
        setFilePath={setFilePath}
        isStorageTable={isStorageTable}
        hasLoadingError={hasLoadingError}
      />
    </>
  ) : (
    <Tailwind>
      <div className="file-browser">
        {/* render the path */}
        <Header className="file-browser-path">
          &gt;&nbsp;
          {breadcrumbs.map((folderName, i) => {
            let seperator;
            if (i !== breadcrumbs.length - 1) {
              seperator = ' / ';
            }
            return [
              <span
                className="file-browser-path-item cursor-pointer"
                key={'path' + i}
                onClick={e => {
                  setFilePath(path.slice(0, i));
                  if (i === 0) {
                    resetQuery?.();
                  }
                }}>
                {folderName}
              </span>,
              seperator,
            ];
          })}
          {deleteButton}
        </Header>
        {currentFolder != null && !loadingFiles && (
          <Folder
            folderStatus={selectionStatus}
            onSearchChange={onSearchChange}
            loadingFiles={loadingFiles}
            loadingProjects={loadingProjects}
            loadingArtifacts={loadingArtifacts}
            folder={currentFolder}
            path={path}
            setFilePath={setFilePath}
          />
        )}
      </div>
    </Tailwind>
  );
};

export default FileBrowser;

interface FileMeta {
  id: string;
  bytes: number;
  type: 'file' | 'artifact_version' | 'artifact';
}

// TODO: this is an awfully hacky way of deriving the type
function typeFromFile(file: File) {
  if (file.artifact == null) {
    return 'file';
  }
  return file.name.includes(':v') ? 'artifact_version' : 'artifact';
}

function recurseFiles(root: Node): FileMeta[] {
  const subFolderFiles = Object.keys(root.subdirectories).flatMap(k =>
    recurseFiles(root.subdirectories[k])
  );
  return subFolderFiles.concat(
    root.files.map(f => ({
      id: f.id,
      bytes: f.sizeBytes,
      type: typeFromFile(f),
    }))
  );
}

const StorageFileBrowserPaginationComp: React.FC<{
  displayOffset: number;
  foldersAndFilesLength: number;
  setDisplayOffset: (offset: number) => void;
}> = ({displayOffset, foldersAndFilesLength, setDisplayOffset}) => {
  return (
    <div className="flex justify-center">
      <Pagination
        activePage={displayOffset / PAGE_SIZE + 1}
        totalPages={Math.ceil(foldersAndFilesLength / PAGE_SIZE)}
        onPageChange={(e, data) => {
          const pg = data.activePage;
          if (pg != null && _.isNumber(pg)) {
            setDisplayOffset((pg - 1) * PAGE_SIZE);
          }
        }}
        size="small"
      />
    </div>
  );
};

const StorageFileBrowserPagination = React.memo(
  StorageFileBrowserPaginationComp
);

type FolderProps = {
  loadingFiles?: boolean;
  loadingProjects?: boolean;
  loadingArtifacts?: boolean;
  folder: Node;
  path: string[];
  folderStatus?: string;
  isStorageTable?: boolean;
  hasLoadingError?: boolean;
  onSearchChange?(e: React.MouseEvent, search: SearchProps): void;
  setFilePath(path: string[], nullProject?: boolean, nullRun?: boolean): void;
};

const Folder = (props: FolderProps) => {
  const {
    folderStatus,
    folder,
    path,
    loadingProjects,
    loadingArtifacts,
    loadingFiles,
    setFilePath,
    onSearchChange,
    isStorageTable = false,
    hasLoadingError = false,
  } = props;
  const [displayOffset, setDisplayOffset] = useState(0);
  const numSubdirectories = Object.keys(folder.subdirectories).length;
  useEffect(() => {
    setDisplayOffset(0);
  }, [folder.id, numSubdirectories]);
  const state = useContext(StorageTableContext);
  const dispatch = useContext(StorageTableDispatchContext);

  // separate subfolders from files in this directory
  const rootFiles = folder.files;
  const subfolderKeys = _.sortBy(
    Object.keys(folder.subdirectories),
    key => -(folder.subdirectories[key].size || 0)
  );
  const foldersAndFiles = [...subfolderKeys, ...rootFiles];
  // TODO: do we really want disabled to mean checked?
  function selectedOrDisabled(node: Node | File) {
    return node.selected || node.disabled;
  }
  const allSelected =
    selectedOrDisabled(folder) ||
    foldersAndFiles
      .slice(displayOffset, displayOffset + PAGE_SIZE)
      .every(f =>
        selectedOrDisabled(_.isString(f) ? folder.subdirectories[f] : f)
      );

  const subdirectories = Object.values(folder.subdirectories);
  const subdirectoriesAllDisabled =
    subdirectories.length > 0 &&
    subdirectories.every(
      subdirectory =>
        subdirectory.disabled === true || subdirectory.selectable === false
    );
  const filesAllDisabled =
    rootFiles.length > 0 && rootFiles.every(file => file.disabled === true);
  const selectAllDisabled =
    folder.disabled === true || subdirectoriesAllDisabled || filesAllDisabled;

  const onClickSelectAll = (checked: boolean) => {
    if (!selectAllDisabled) {
      foldersAndFiles
        .slice(displayOffset, displayOffset + PAGE_SIZE)
        .forEach(folderOrFile => {
          if (_.isString(folderOrFile)) {
            const subFolder = folder.subdirectories[folderOrFile];
            if (subFolder.disabled !== true) {
              let ids = [subFolder.id!];
              let bytes = [subFolder.size!];
              let types = [subFolder.type!];
              if (subFolder.id == null) {
                const files = recurseFiles(subFolder);
                ids = files.map(file => file.id);
                bytes = files.map(file => file.bytes);
                types = files.map(file => file.type);
              }
              dispatch({
                type: checked ? 'select' : 'deselect',
                payload: {
                  ids,
                  bytes,
                  types,
                  path: path.concat([subFolder?.name ?? '']),
                },
              });
            }
          } else {
            const file = folderOrFile;
            const fileName = file.name.split('/').pop() || '';
            const newPath = path.concat([fileName]);
            const type = typeFromFile(file);
            dispatch({
              type: checked ? 'select' : 'deselect',
              payload: {
                ids: [file.id],
                bytes: [file.sizeBytes],
                types: [type],
                path: newPath,
              },
            });
          }
        });
    }
  };

  const headerCells = {
    [HeaderCellType.CHECKBOX]: {
      columnLabel: selectAllDisabled ? (
        <div className="w-24" />
      ) : (
        <TableCheckbox
          disabled={selectAllDisabled}
          checked={allSelected && foldersAndFiles.length > 0}
          onCheckedChange={onClickSelectAll}
        />
      ),
      classNameOverride: clsx(
        selectAllDisabled ? 'cursor-auto' : 'cursor-pointer',
        'justify-center rounded-none p-8',
        HEADER_BACKGROUND
      ),
    },
    ...DEFAULT_HEADER_CELLS,
  };

  const sortStorageItems = (
    folderOrFileA: string | File,
    folderOrFileB: string | File
  ) => {
    const getValueFunctions = headerCells[state.sortColumn]?.getValueFunctions;
    if (getValueFunctions != null) {
      const getValue = (folderOrFile: string | File) =>
        _.isString(folderOrFile)
          ? getValueFunctions.folder(folder.subdirectories[folderOrFile])
          : getValueFunctions.file(folderOrFile);
      const valueA = getValue(folderOrFileA);
      const valueB = getValue(folderOrFileB);

      const comparison = DEFAULT_COMPARE_FUNCTION(valueA, valueB);
      if (!state.isAsc) {
        return -1 * comparison;
      }
      return comparison;
    }
    return 0;
  };

  let storageTableBody = null;
  const isLoading = loadingProjects || loadingArtifacts || loadingFiles;
  if (foldersAndFiles.length === 0 && isLoading) {
    storageTableBody = <StorageTableRowLoading />;
  }
  if (foldersAndFiles.length === 0 && !isLoading && !hasLoadingError) {
    storageTableBody = <EmptyStorageTable />;
  }
  if (foldersAndFiles.length === 0 && hasLoadingError) {
    storageTableBody = <StorageTableLoadingFailure />;
  }
  if (foldersAndFiles.length > 0) {
    storageTableBody = foldersAndFiles
      .slice(displayOffset, displayOffset + PAGE_SIZE)
      .sort(sortStorageItems)
      .map(folderOrFile => {
        if (_.isString(folderOrFile)) {
          return (
            <SubFolder
              key={'folder-' + folderOrFile}
              folder={folder.subdirectories[folderOrFile]}
              folderName={folderOrFile}
              path={path}
              setFilePath={setFilePath}
              isStorageTable={isStorageTable}
            />
          );
        } else {
          return (
            <FileRow
              key={'file-' + folderOrFile.id}
              file={folderOrFile}
              path={path}
              isStorageTable={isStorageTable}
            />
          );
        }
      });
  }

  return isStorageTable ? (
    <>
      <StorageList headerCells={headerCells}>
        <StorageTableBody>{storageTableBody}</StorageTableBody>
      </StorageList>
      {foldersAndFiles.length > PAGE_SIZE && (
        <StorageFileBrowserPagination
          displayOffset={displayOffset}
          foldersAndFilesLength={foldersAndFiles.length}
          setDisplayOffset={setDisplayOffset}
        />
      )}
    </>
  ) : (
    <>
      <Table
        unstackable
        selectable
        className="file-browser-table storage-explorer">
        <Table.Header>
          <Table.Row>
            <Table.Cell className="file-selector-cell">
              {folder.selectable && (
                <Checkbox
                  checked={allSelected}
                  disabled={folder.disabled}
                  onChange={(e, {checked}) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (folder.id != null) {
                      dispatch!({
                        type: checked ? 'select' : 'deselect',
                        payload: {
                          path,
                          ids: [folder.id],
                          bytes: [folder.size || 0],
                          types: [folder.type!],
                        },
                      });
                    } else {
                      // TODO: are there any cases where I need to support just sub-folders?
                      const files = recurseFiles(folder);
                      dispatch!({
                        type: checked ? 'select' : 'deselect',
                        payload: {
                          path,
                          ids: files.map(file => file.id),
                          bytes: files.map(file => file.bytes),
                          types: files.map(file => file.type),
                        },
                      });
                    }
                  }}
                />
              )}
            </Table.Cell>
            <Table.Cell className="info">
              {folderStatus || folder.summary}
            </Table.Cell>
            {folder.searchable && (
              <Table.Cell className="search" colSpan="3" textAlign="right">
                <div className="flex flex-row-reverse items-center">
                  <Search
                    category
                    open={false}
                    onSearchChange={onSearchChange}
                    results={[]}
                  />
                  {loadingProjects && (
                    <div className="mr-12">
                      <WandbLoader
                        name="loading-projects"
                        inline
                        size={'small'}
                      />
                    </div>
                  )}
                </div>
              </Table.Cell>
            )}
            {!folder.searchable && !loadingProjects && loadingArtifacts && (
              <Table.Cell colSpan="3" textAlign="right">
                <div className="ml-12">
                  <WandbLoader name="loading-artifacts" inline size={'small'} />
                </div>
              </Table.Cell>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {foldersAndFiles
            .slice(displayOffset, displayOffset + PAGE_SIZE)
            .map((folderOrFile, i) => {
              if (_.isString(folderOrFile)) {
                return (
                  <SubFolder
                    key={'folder-' + folderOrFile}
                    folder={folder.subdirectories[folderOrFile]}
                    folderName={folderOrFile}
                    path={path}
                    setFilePath={setFilePath}
                  />
                );
              } else {
                return (
                  <FileRow
                    key={'file-' + folderOrFile.id}
                    file={folderOrFile}
                    path={path}
                  />
                );
              }
            })}
        </Table.Body>
      </Table>
      {foldersAndFiles.length > PAGE_SIZE && (
        <StorageFileBrowserPagination
          displayOffset={displayOffset}
          foldersAndFilesLength={foldersAndFiles.length}
          setDisplayOffset={setDisplayOffset}
        />
      )}
    </>
  );
};

interface SubFolderProps {
  folder: Node;
  path: string[];
  folderName: string;
  isStorageTable?: boolean;
  setFilePath(path: string[]): void;
}

const SubFolder = (props: SubFolderProps) => {
  const {folder, folderName, path, setFilePath, isStorageTable = false} = props;
  const newPath = path.concat([folderName]);
  const size = folder.size;
  const dispatch = useContext(StorageTableDispatchContext);

  return isStorageTable ? (
    <SelectableTableRow
      onClick={() => setFilePath(newPath)}
      isSelected={folder.selected === true || folder.disabled === true}
      cells={[
        {
          component: folder.selectable && (
            <TableCheckbox
              checked={(folder.selected || folder.disabled) === true}
              disabled={folder.disabled}
              onCheckedChange={checked => {
                if (folder.disabled === false) {
                  let ids = [folder.id!];
                  let bytes = [folder.size!];
                  let types = [folder.type!];
                  if (folder.id == null) {
                    const files = recurseFiles(folder);
                    ids = files.map(file => file.id);
                    bytes = files.map(file => file.bytes);
                    types = files.map(file => file.type);
                  }
                  dispatch({
                    type: checked ? 'select' : 'deselect',
                    payload: {
                      ids,
                      bytes,
                      types,
                      path: newPath,
                    },
                  });
                }
              }}
            />
          ),
          classNameOverride: clsx(
            folder.disabled ? 'cursor-auto' : 'cursor-pointer',
            'justify-center p-8 min-w-[40px]'
          ),
        },
        {
          component: folder.name,
          iconName: IconNames.FolderFill,
          iconClassName: 'text-teal-500',
        },
        {component: numeral(size).format('0.0b')},
      ]}
    />
  ) : (
    <Table.Row
      className={clsx('file-browser-folder', {
        'child-selected': folder.childSelected,
      })}
      onClick={() => setFilePath(newPath)}>
      <Table.Cell
        className="file-selector-cell"
        // TODO: make this cancel the above click
        onClick={(e: React.SyntheticEvent) => {
          e.stopPropagation();
        }}>
        {folder.selectable && (
          <Checkbox
            checked={(folder.selected || folder.disabled) === true}
            disabled={folder.disabled}
            onClick={e => e.stopPropagation()}
            onChange={(e, {checked}) => {
              e.preventDefault();
              e.stopPropagation();
              let ids = [folder.id!];
              let bytes = [folder.size!];
              let types = [folder.type!];
              if (folder.id == null) {
                const files = recurseFiles(folder);
                ids = files.map(file => file.id);
                bytes = files.map(file => file.bytes);
                types = files.map(file => file.type);
              }
              dispatch({
                type: checked ? 'select' : 'deselect',
                payload: {
                  ids,
                  bytes,
                  types,
                  path: newPath,
                },
              });
            }}
          />
        )}
      </Table.Cell>
      <Table.Cell className="folder-name-cell">
        <div className="file-browser-name-cell-wrapper">
          <LegacyWBIcon className="file-browser-icon" name="folder" />
          <span className="file-browser-folder-name">{folderName}</span>
          &nbsp;/
        </div>
      </Table.Cell>
      <Table.Cell className="contents-cell">{folder.summary}</Table.Cell>
      <Table.Cell className="file-size-cell">
        {numeral(size).format('0.0b')}
      </Table.Cell>
      <Table.Cell />
    </Table.Row>
  );
};

type FileProps = {
  file: File;
  path: string[];
  isStorageTable?: boolean;
};

const FileRow = (props: FileProps) => {
  const {file, path, isStorageTable = false} = props;
  const dispatch = useContext(StorageTableDispatchContext);
  const fileName = file.name.split('/').pop() || '';
  const newPath = path.concat([fileName]);
  const fileInfo = fileInfoFromName(fileName);
  const iconName = fileInfo.iconName;

  function selectFile(e: React.SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!file.disabled) {
      const checked = !file.selected;
      const type = typeFromFile(file);
      dispatch({
        type: checked ? 'select' : 'deselect',
        payload: {
          ids: [file.id],
          bytes: [file.sizeBytes],
          types: [type],
          path: newPath,
        },
      });
    }
  }

  return isStorageTable ? (
    <SelectableTableRow
      isSelected={file.selected === true || file.disabled === true}
      cells={[
        {
          component: (
            <TableCheckbox
              checked={(file.selected || file.disabled) === true}
              onCheckedChange={checked => {
                if (!file.disabled) {
                  const type = typeFromFile(file);
                  dispatch({
                    type: checked ? 'select' : 'deselect',
                    payload: {
                      ids: [file.id],
                      bytes: [file.sizeBytes],
                      types: [type],
                      path: newPath,
                    },
                  });
                }
              }}
              disabled={file.disabled}
            />
          ),
          classNameOverride: clsx(
            'justify-center m-8',
            file.disabled ? 'cursor-auto' : 'cursor-pointer'
          ),
        },
        {
          component: fileName,
          iconName: IconNames.Document,
        },
        {
          component: (
            <div className="flex w-full justify-between">
              {numeral(file.sizeBytes).format('0.0b')}
              {file.url != null && (
                <div className="flex h-full items-center justify-center">
                  <a
                    href={
                      file.ref != null
                        ? file.ref
                        : Url.encodeURIPercentChar(file.url)
                    }
                    download={file.name}
                    onClick={e => e.stopPropagation()}>
                    <WeaveIcon name="download" width={18} height={18} />
                  </a>
                </div>
              )}
            </div>
          ),
        },
      ]}
    />
  ) : (
    <Table.Row onClick={selectFile}>
      <Table.Cell className="file-selector-cell">
        <Checkbox
          checked={(file.selected || file.disabled) === true}
          disabled={file.disabled}
          onClick={e => e.stopPropagation()}
          onChange={selectFile}
        />
      </Table.Cell>
      <Table.Cell className="file-name-cell">
        <div className="file-browser-name-cell-wrapper">
          {file.ref != null ? (
            <Icon
              className="text-primary w-28"
              name="arrow alternate circle right outline"
            />
          ) : (
            <LegacyWBIcon className="file-browser-icon" name={iconName} />
          )}
          <span className="file-browser-file-name">
            {file.name.split('/').pop()}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell className="updated-time-cell">
        {file.ref != null ? (
          file.ref
        ) : file.updatedAt != null ? (
          <TimeAgo date={file.updatedAt} />
        ) : undefined}
      </Table.Cell>
      <Table.Cell className="file-size-cell">
        {numeral(file.sizeBytes).format('0.0b')}
      </Table.Cell>
      <Table.Cell
        className="hide-in-mobile"
        onClick={(e: Event) => e.stopPropagation()}>
        {file.url != null && (
          <a
            href={
              file.ref != null ? file.ref : Url.encodeURIPercentChar(file.url)
            }
            download={file.name}
            onClick={e => e.stopPropagation()}>
            <LegacyWBIcon name="download" />
          </a>
        )}
      </Table.Cell>
    </Table.Row>
  );
};
